/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, OauthClientResponse, UpdateOauthClientRequest } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UpdateOAuthClientMutationConfig = {
  onSuccess?: (data: OauthClientResponse, variables: { id: string } & UpdateOauthClientRequest) => void;
  onError?: (error: unknown, variables: { id: string } & UpdateOauthClientRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: { id: string } & UpdateOauthClientRequest) => Promise<unknown> | void;
};

/**
 * Hook for updating an OAuth client through the management plane API.
 *
 * @param config.onSuccess - Called after successful OAuth client update
 * @param config.onError - Called if update fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation executes
 *
 * @returns React Query mutation result for updating OAuth clients
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateOAuthClientMutation({
 *   onSuccess: (data) => console.log('OAuth client updated:', data),
 *   onError: (error) => console.error('Update failed:', error)
 * });
 *
 * // Update OAuth client
 * mutate({
 *   id: 'client-id',
 *   name: 'Updated Name',
 *   redirectUris: ['https://example.com/callback']
 * });
 * ```
 */
export function useUpdateOAuthClientMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: UpdateOAuthClientMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    OauthClientResponse,
    ApiError,
    { id: string } & UpdateOauthClientRequest
  >({
    mutationFn: async (client: { id: string } & UpdateOauthClientRequest) => {
      return mgmtPlaneAPIClient.oauthClients.updateOauthClient({
        tenantId: tenantId!,
        oauthClientId: client.id,
        requestBody: {
          name: client.name,
          redirectUris: client.redirectUris,
        },
      });
    },
    onSuccess: (data, variables) => {
      // Manually invalidate the OAuth clients query cache
      queryClient.invalidateQueries({ queryKey: ['oauthClients'] });

      // Call the user-provided onSuccess callback
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error, isPending };
}
